import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, ClipboardCheck, CalendarDays, Award, Inbox, LogOut, Menu, X,
  FileText, Medal, Star, HandHeart, Cake, ShieldCheck, GraduationCap, MessageCircle, Target,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { WelcomeDialog } from "@/components/WelcomeDialog";
import { SupportButton } from "@/components/SupportButton";
import { NotificationBell } from "@/components/NotificationBell";
import { WelcomeTour, openTour } from "@/components/WelcomeTour";
import { ContextHints } from "@/components/ContextHints";
import { HelpCircle, LifeBuoy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/dashboard", label: "Painel", icon: LayoutDashboard, module: "dashboard", testid: "nav-dashboard" },
  { to: "/grupos", label: "Grupos & Líderes", icon: Users, module: "grupos", testid: "nav-grupos" },
  { to: "/frequencia", label: "Frequência", icon: ClipboardCheck, module: "frequencia", testid: "nav-frequencia" },
  { to: "/aniversariantes", label: "Aniversariantes", icon: Cake, module: "aniversariantes", testid: "nav-aniversariantes" },
  { to: "/oracoes", label: "Pedidos de Oração", icon: HandHeart, module: "oracoes", testid: "nav-oracoes" },
  { to: "/calendario", label: "Calendário", icon: CalendarDays, module: "calendario", testid: "nav-calendario" },
  { to: "/formacao", label: "Estudos & Formação", icon: GraduationCap, module: "formacao", testid: "nav-formacao" },
  { to: "/atendimento", label: "Atendimento", icon: MessageCircle, module: "atendimento", testid: "nav-atendimento" },
  { to: "/metas", label: "Metas & Compromissos", icon: Target, module: "metas", testid: "nav-metas" },
  { to: "/relatorios", label: "Relatório Pastoral", icon: FileText, module: "relatorios", testid: "nav-relatorios" },
  { to: "/certificados", label: "Certificados", icon: Medal, module: "certificados", testid: "nav-certificados" },
  { to: "/pesquisas", label: "Pesquisas", icon: Star, module: "pesquisas", testid: "nav-pesquisas" },
  { to: "/celebracao", label: "Celebração", icon: Award, module: "celebracao", testid: "nav-celebracao" },
  { to: "/contatos", label: "Contatos", icon: Inbox, module: "contatos", testid: "nav-contatos" },
];

const ROLE_LABEL = { coordenador: "Coordenador", coordenador_geral: "Coordenador Geral", supervisor: "Supervisor", lider: "Líder de PGD", secretario: "Secretário(a)", anfitriao: "Anfitrião(ã)", membro: "Membro", visitante: "Visitante" };

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isAdmin = !!user?.is_admin || user?.role === "coordenador";
  const allowed = user?.allowed_modules || [];
  const visibleNav = NAV.filter((item) => isAdmin || allowed.includes(item.module));

  const SidebarInner = (
    <div className="flex flex-col h-full">
      <div className="px-5 py-6 border-b border-border">
        <Logo />
      </div>
      <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
        {visibleNav.map(({ to, label, icon: Icon, testid }) => {
          const active = location.pathname === to;
          return (
            <Link
              key={to}
              to={to}
              data-testid={testid}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                active
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}
            >
              <Icon className="h-[18px] w-[18px]" />
              {label}
            </Link>
          );
        })}
        {isAdmin && (
          <Link
            to="/usuarios"
            data-testid="nav-usuarios"
            onClick={() => setOpen(false)}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              location.pathname === "/usuarios"
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:bg-secondary hover:text-foreground"
            }`}
          >
            <ShieldCheck className="h-[18px] w-[18px]" />
            Usuários & Acessos
          </Link>
        )}
      </nav>
      <div className="px-4 py-4 border-t border-border">
        <button onClick={() => { setOpen(false); openTour(); }} data-testid="nav-tour"
          className="flex w-full items-center gap-3 px-3 py-2.5 mb-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors">
          <HelpCircle className="h-[18px] w-[18px]" /> Tour do app
        </button>
        <Link to="/ajuda" onClick={() => setOpen(false)} data-testid="nav-ajuda"
          className="flex w-full items-center gap-3 px-3 py-2.5 mb-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors">
          <LifeBuoy className="h-[18px] w-[18px]" /> Central de Ajuda
        </Link>
        <Link to="/perfil" onClick={() => setOpen(false)} className="flex items-center gap-3 mb-3 rounded-lg hover:bg-secondary p-2 -m-2 transition-colors" data-testid="sidebar-profile-link">
          {user?.avatar_path ? (
            <img src={`${API}/user-photo/${user.avatar_path}`} alt="avatar" className="h-9 w-9 rounded-full object-cover" />
          ) : (
            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-accent to-primary flex items-center justify-center text-sm font-bold text-white">
              {user?.name?.charAt(0)?.toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-[11px] text-muted-foreground">{ROLE_LABEL[user?.role] || user?.role}</div>
          </div>
        </Link>
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-start gap-2"
          onClick={handleLogout}
          data-testid="logout-button"
        >
          <LogOut className="h-4 w-4" /> Sair
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background grain">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 border-r border-border bg-card/60 backdrop-blur-md flex-col z-30">
        {SidebarInner}
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between px-4 py-3 border-b border-border bg-card/80 backdrop-blur-md">
        <Logo compact />
        <div className="flex items-center gap-1">
          <button onClick={openTour} data-testid="help-button-mobile" className="p-2 text-muted-foreground">
            <HelpCircle className="h-6 w-6" />
          </button>
          <NotificationBell />
          <button onClick={() => setOpen(true)} data-testid="mobile-menu-button" className="p-2">
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Desktop notification bell (fixed top-right) */}
      <div className="hidden lg:flex items-center gap-2 fixed top-4 right-6 z-40">
        <button onClick={openTour} data-testid="help-button" aria-label="Rever o tour do app"
          className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-card/80 backdrop-blur-md border border-border shadow-sm text-muted-foreground hover:text-foreground transition-colors">
          <HelpCircle className="h-5 w-5" />
        </button>
        <div className="rounded-full bg-card/80 backdrop-blur-md border border-border shadow-sm">
          <NotificationBell />
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 bg-card border-r border-border">
            <button onClick={() => setOpen(false)} className="absolute top-4 right-4 p-1">
              <X className="h-5 w-5" />
            </button>
            {SidebarInner}
          </div>
        </div>
      )}

      <main className="lg:pl-64">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</div>
      </main>
      <WelcomeDialog />
      <WelcomeTour />
      <ContextHints />
      <SupportButton />
    </div>
  );
}
